import React, { useState, useEffect, useRef } from 'react';
import { getProjects, createProject, deleteProject, updateProject,
         getTags, createTag, updateTag, deleteTag, assignTag, removeTag,
         getPackages, deletePackage, BASE_URL } from '../api';
import { useTheme } from '../ThemeContext';
import { useOnboarding } from '../OnboardingContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import ProfileMenu from '../components/ProfileMenu';

const TAG_COLOURS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444',
  '#3b82f6', '#8b5cf6', '#ec4899', '#64748b',
];

export default function Dashboard() {
  const { colors, common, mode } = useTheme();
  const toast = useToast();
  const { openOnboarding } = useOnboarding();

  const [projects, setProjects]             = useState([]);
  const [showModal, setShowModal]           = useState(false);
  const [editProject, setEditProject]       = useState(null);
  const [title, setTitle]                   = useState('');
  const [description, setDescription]       = useState('');
  const [error, setError]                   = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  const [tags, setTags]                     = useState([]);
  const [activeFilters, setActiveFilters]   = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName]         = useState('');
  const [newTagColor, setNewTagColor]       = useState(TAG_COLOURS[0]);
  const [tagError, setTagError]             = useState('');
  const [editingTagId, setEditingTagId]     = useState(null);

  const [packages, setPackages]             = useState([]);
  const [exportingPkg, setExportingPkg]     = useState(null);
  const [exportingPkgScorm, setExportingPkgScorm] = useState(null);

  const [helpTopic, setHelpTopic]           = useState(null); // 'simulations' | 'packages'
  const [confirmAction, setConfirmAction]   = useState(null); // { message, onConfirm }

  useEffect(() => { fetchProjects(); fetchTags(); fetchPackages(); }, []);

  // Show the onboarding wizard automatically the first time a user lands on the dashboard
  useEffect(() => {
    if (!localStorage.getItem('simbuilder-onboarding-seen')) {
      openOnboarding();
    }
  }, [openOnboarding]);

  const fetchProjects = async () => {
    try {
      const { data } = await getProjects();
      setProjects(data);
    } catch (err) {
      console.error('Could not load simulations');
    }
  };

  const fetchTags = async () => {
    try {
      const { data } = await getTags();
      setTags(data);
    } catch (err) {
      console.error('Could not load tags');
    }
  };

  const fetchPackages = async () => {
    try {
      const { data } = await getPackages();
      setPackages(data);
    } catch (err) {
      console.error('Could not load packages');
    }
  };

  const openCreateModal = () => {
    setEditProject(null); setTitle(''); setDescription('');
    setError(''); setSelectedTagIds([]); setShowModal(true);
  };

  const openEditModal = (project) => {
    setEditProject(project); setTitle(project.title);
    setDescription(project.description || ''); setError('');
    setSelectedTagIds((project.tags || []).map(t => t.id));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false); setEditProject(null);
    setTitle(''); setDescription(''); setError(''); setSelectedTagIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editProject) {
        await updateProject(editProject.id, { title, description });
        const previousTagIds = (editProject.tags || []).map(t => t.id);
        const toAdd    = selectedTagIds.filter(id => !previousTagIds.includes(id));
        const toRemove = previousTagIds.filter(id => !selectedTagIds.includes(id));
        await Promise.all([
          ...toAdd.map(tagId    => assignTag(editProject.id, tagId)),
          ...toRemove.map(tagId => removeTag(editProject.id, tagId)),
        ]);
      } else {
        const { data: newProject } = await createProject({ title, description });
        await Promise.all(selectedTagIds.map(tagId => assignTag(newProject.id, tagId)));
      }
      closeModal(); fetchProjects();
    } catch (err) {
      setError(editProject ? 'Could not update simulation' : 'Could not create simulation');
    }
  };

  const handleDelete = (id) => {
    setConfirmAction({
      message: 'Are you sure you want to delete this simulation? This cannot be undone.',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await deleteProject(id);
          fetchProjects();
        } catch (err) {
          toast('Could not delete simulation. Please try again.', 'error');
        }
      },
    });
  };

  const handleDeletePackage = (id) => {
    setConfirmAction({
      message: 'Are you sure you want to delete this package? This cannot be undone.',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await deletePackage(id);
          fetchPackages();
        } catch (err) {
          toast('Could not delete package. Please try again.', 'error');
        }
      },
    });
  };

  const handleExportPackage = async (pkg) => {
    setExportingPkg(pkg.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/packages/${pkg.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${pkg.title || 'package'}_package.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('Export failed. Please try again.', 'error'); }
    setExportingPkg(null);
  };

  const handleExportPackageScorm = async (pkg) => {
    setExportingPkgScorm(pkg.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/packages/${pkg.id}/export/scorm`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${pkg.title || 'package'}_package_scorm.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('SCORM export failed. Please try again.', 'error'); }
    setExportingPkgScorm(null);
  };

  const toggleFilter = (tagId) => {
    setActiveFilters(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const toggleTagSelection = (tagId) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    setTagError('');
    try {
      await createTag({ name: newTagName.trim(), color: newTagColor });
      setNewTagName(''); setNewTagColor(TAG_COLOURS[0]);
      fetchTags();
    } catch (err) {
      setTagError(err.response?.data?.error || 'Could not create tag');
    }
  };

  const handleUpdateTagColor = async (tagId, color) => {
    try {
      await updateTag(tagId, { color });
      setTags(prev => prev.map(t => (t.id === tagId ? { ...t, color } : t)));
      setEditingTagId(null);
      fetchProjects(); // refresh tag chips shown on the cards
    } catch (err) {
      toast('Could not update tag colour. Please try again.', 'error');
    }
  };

  const handleDeleteTag = (tagId) => {
    setConfirmAction({
      message: 'Delete this tag? It will be removed from all simulations.',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await deleteTag(tagId);
          setTags(prev => prev.filter(t => t.id !== tagId));
          setActiveFilters(prev => prev.filter(id => id !== tagId));
          fetchProjects();
        } catch (err) {
          toast('Could not delete tag. Please try again.', 'error');
        }
      },
    });
  };

  const filteredProjects = activeFilters.length === 0
    ? projects
    : projects.filter(p => (p.tags || []).some(t => activeFilters.includes(t.id)));

  const tagChip = (color) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: 12,
    fontSize: 11, fontWeight: 600, color: '#fff',
    background: color, marginRight: 4, marginBottom: 4,
  });

  const filterChip = (color, active) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '4px 12px', borderRadius: 20,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: `2px solid ${color}`,
    background: active ? color : 'transparent',
    color: active ? '#fff' : colors.textPrimary,
    transition: 'all 0.15s',
  });

  const allFilterChip = {
    display: 'inline-flex', alignItems: 'center',
    padding: '4px 12px', borderRadius: 20,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: `2px solid ${colors.textMuted}`,
    background: activeFilters.length === 0 ? colors.textMuted : 'transparent',
    color: activeFilters.length === 0 ? '#fff' : colors.textMuted,
    transition: 'all 0.15s',
  };

  const colorSwatch = (color, selected) => ({
    width: 22, height: 22, borderRadius: '50%', background: color, cursor: 'pointer',
    border: selected ? `3px solid ${colors.textPrimary}` : '3px solid transparent',
    boxSizing: 'border-box', flexShrink: 0,
  });

  return (
    <div style={{ ...common.page, minHeight: 'unset', flex: 1 }}>
      <header style={common.header}>
        <a href="/dashboard" style={{ ...common.appTitle, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={mode === 'dark' ? '/Logo-Dark.png' : '/Logo-Light.png'} alt="Software Simulation Builder logo" style={{ height: 36, width: 'auto' }} />
          Software Simulation Builder
        </a>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button style={{ ...common.btn, ...common.btnGhost }} onClick={() => setShowTagManager(true)}>
            Manage Tags
          </button>
          <ProfileMenu />
        </div>
      </header>

      <main id="main-content" style={common.content}>
        <h1 className="sr-only">Dashboard</h1>

        {/* ── Simulations section ── */}
        <div style={common.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={common.pageHeading}>Your Simulations</h2>
            <button onClick={() => setHelpTopic('simulations')} aria-label="Help: Simulations"
              style={styles.helpBtn(colors)}>?</button>
          </div>
          <button style={{ ...common.btn, ...common.btnPrimary }} onClick={openCreateModal}>
            + New Simulation
          </button>
        </div>

        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
            <button style={allFilterChip} onClick={() => setActiveFilters([])}
              aria-pressed={activeFilters.length === 0}>All</button>
            {tags.map(tag => (
              <button key={tag.id} style={filterChip(tag.color, activeFilters.includes(tag.id))}
                onClick={() => toggleFilter(tag.id)}
                aria-pressed={activeFilters.includes(tag.id)}>
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {filteredProjects.length === 0 ? (
          <div style={common.empty}>
            {projects.length === 0 ? (
              <>
                <p style={{ marginBottom: 8 }}>You don't have any simulations yet.</p>
                <p>Click <strong style={{ color: colors.textPrimary }}>+ New Simulation</strong> to get started!</p>
              </>
            ) : (
              <p>No simulations match the selected tags.</p>
            )}
          </div>
        ) : (
          <div style={styles.grid}>
            {filteredProjects.map((project) => (
              <div key={project.id} style={common.card}>
                <div style={{ height: 4, background: `linear-gradient(90deg, ${colors.indigo}, #7c3aed)` }} />
                <div style={{ padding: 24 }}>
                  <h3 style={{ margin: '0 0 6px 0', color: colors.textPrimary, fontSize: 16, fontWeight: 700 }}>
                    {project.title}
                  </h3>
                  <p style={{ margin: '0 0 8px 0', color: colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
                    {project.description || 'No description'}
                  </p>
                  <p style={{ margin: '0 0 12px 0', color: colors.textMuted, fontSize: 12 }}>
                    Created: {new Date(project.created_at).toLocaleDateString('en-GB')}
                  </p>
                  {(project.tags || []).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      {project.tags.map(tag => (
                        <span key={tag.id} style={tagChip(tag.color)}>{tag.name}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <a href={`/projects/${project.id}`}
                      style={{ ...common.btn, ...common.btnPrimary, flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                      aria-label={`Edit ${project.title}`}>Edit</a>
                    <button style={{ ...common.btn, flex: 1, justifyContent: 'center', backgroundColor: '#475569', color: '#fff' }}
                      onClick={() => openEditModal(project)}>Info</button>
                    <button style={{ ...common.btn, ...common.btnDanger, flex: 1, justifyContent: 'center' }}
                      onClick={() => handleDelete(project.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Packages section ── */}
        <div style={{ ...common.topBar, marginTop: 48 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={common.pageHeading}>Your Packages</h2>
              <button onClick={() => setHelpTopic('packages')} aria-label="Help: Packages"
                style={styles.helpBtn(colors)}>?</button>
            </div>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginTop: 4 }}>
              Combine multiple simulations into a single exportable package
            </p>
          </div>
          <a href="/packages/new"
            style={{ ...common.btn, ...common.btnPrimary, textDecoration: 'none' }}>
            + New Package
          </a>
        </div>

        {packages.length === 0 ? (
          <div style={common.empty}>
            <p style={{ marginBottom: 8 }}>No packages yet.</p>
            <p>Click <strong style={{ color: colors.textPrimary }}>+ New Package</strong> to bundle simulations together!</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {packages.map((pkg) => (
              <div key={pkg.id} style={common.card}>
                <div style={{ height: 4, background: `linear-gradient(90deg, #059669, #0d9488)` }} />
                <div style={{ padding: 24 }}>
                  <h3 style={{ margin: '0 0 6px 0', color: colors.textPrimary, fontSize: 16, fontWeight: 700 }}>
                    {pkg.title}
                  </h3>
                  <p style={{ margin: '0 0 8px 0', color: colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
                    {pkg.description || 'No description'}
                  </p>
                  <p style={{ margin: '0 0 16px 0', color: colors.textMuted, fontSize: 12 }}>
                    {pkg.sim_count} simulation{pkg.sim_count !== 1 ? 's' : ''} · Created: {new Date(pkg.created_at).toLocaleDateString('en-GB')}
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <a href={`/packages/${pkg.id}`}
                      style={{ ...common.btn, ...common.btnPrimary, flex: 1, justifyContent: 'center', textDecoration: 'none', minWidth: 60 }}
                      aria-label={`Edit ${pkg.title}`}>Edit</a>
                    <button
                      style={{ ...common.btn, ...common.btnPrimary, flex: 1, justifyContent: 'center', minWidth: 80, opacity: exportingPkg === pkg.id ? 0.7 : 1 }}
                      onClick={() => handleExportPackage(pkg)} disabled={exportingPkg === pkg.id}>
                      {exportingPkg === pkg.id ? '⏳' : '⬇ HTML'}
                    </button>
                    <button
                      style={{ ...common.btn, ...common.btnPrimary, flex: 1, justifyContent: 'center', minWidth: 80, opacity: exportingPkgScorm === pkg.id ? 0.7 : 1 }}
                      onClick={() => handleExportPackageScorm(pkg)} disabled={exportingPkgScorm === pkg.id}>
                      {exportingPkgScorm === pkg.id ? '⏳' : '⬇ SCORM'}
                    </button>
                    <button style={{ ...common.btn, ...common.btnDanger, flex: 1, justifyContent: 'center', minWidth: 60 }}
                      onClick={() => handleDeletePackage(pkg.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* ── Create / Edit simulation modal ── */}
      {showModal && (
        <div style={common.overlay}>
          <div style={common.modal}>
            <h2 style={common.modalTitle}>{editProject ? 'Edit Simulation' : 'New Simulation'}</h2>
            {error && <p role="alert" style={common.errorText}>{error}</p>}
            <form onSubmit={handleSubmit}>
              <label htmlFor="project-title" style={common.label}>Simulation Title</label>
              <input id="project-title" style={common.input} type="text" placeholder="e.g. Photoshop Simulation"
                value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
              <label htmlFor="project-description" style={common.label}>Description (optional)</label>
              <textarea id="project-description" style={common.textarea} placeholder="What does this simulation cover?"
                value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
              {tags.length > 0 && (
                <>
                  <label style={common.label}>Tags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {tags.map(tag => {
                      const selected = selectedTagIds.includes(tag.id);
                      return (
                        <button key={tag.id} type="button"
                          style={{
                            ...common.btn,
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            border: `2px solid ${tag.color}`,
                            background: selected ? tag.color : 'transparent',
                            color: selected ? '#fff' : colors.textPrimary,
                          }}
                          onClick={() => toggleTagSelection(tag.id)}>
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <div style={common.modalActions}>
                <button style={{ ...common.btn, ...common.btnGhost }} type="button" onClick={closeModal}>Cancel</button>
                <button style={{ ...common.btn, ...common.btnPrimary }} type="submit">
                  {editProject ? 'Save Changes' : 'Create Simulation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Help modals ── */}
      {helpTopic && (
        <HelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} colors={colors} common={common} mode={mode} />
      )}

      {/* ── Confirm dialog ── */}
      {confirmAction && (
        <ConfirmDialog
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* ── Tag manager modal ── */}
      {showTagManager && (
        <div style={common.overlay}>
          <div style={{ ...common.modal, maxWidth: 440 }}>
            <h2 style={common.modalTitle}>Manage Tags</h2>

            <div style={{
              backgroundColor: colors.hoverBg,
              border: `1px solid ${colors.borderColor}`,
              borderLeft: `4px solid ${colors.indigo}`,
              borderRadius: 8, padding: '10px 14px', marginBottom: 20,
              fontSize: 13, color: colors.textSecondary, lineHeight: 1.6,
            }}>
              <strong style={{ color: colors.textPrimary, display: 'block', marginBottom: 2 }}>What are tags?</strong>
              Tags let you group and organise your simulations — for example by software, department, or course.
              Once assigned to a simulation, you can filter by tag in the Screenshot Manager to quickly find related screens.
            </div>

            <form onSubmit={handleCreateTag} style={{ marginBottom: 24 }}>
              <label htmlFor="new-tag-name" style={common.label}>New Tag</label>
              {tagError && <p role="alert" style={common.errorText}>{tagError}</p>}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input id="new-tag-name" style={{ ...common.input, flex: 1, marginBottom: 0 }}
                  type="text" placeholder="e.g. Photoshop"
                  value={newTagName} onChange={(e) => setNewTagName(e.target.value)} required />
                <button style={{ ...common.btn, ...common.btnPrimary }} type="submit">Add</button>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TAG_COLOURS.map(c => (
                  <button key={c} type="button" style={colorSwatch(c, newTagColor === c)}
                    onClick={() => setNewTagColor(c)} title={c} />
                ))}
              </div>
            </form>

            {tags.length === 0 ? (
              <p style={{ color: colors.textMuted, fontSize: 14 }}>No tags yet. Create one above.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tags.map(tag => (
                  <div key={tag.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button type="button" title="Change colour"
                        onClick={() => setEditingTagId(editingTagId === tag.id ? null : tag.id)}
                        style={{ width: 16, height: 16, borderRadius: '50%', background: tag.color,
                          flexShrink: 0, border: `2px solid ${colors.borderColor}`, cursor: 'pointer', padding: 0 }} />
                      <span style={{ flex: 1, color: colors.textPrimary, fontSize: 14 }}>{tag.name}</span>
                      <button style={{ ...common.btn, padding: '2px 10px', fontSize: 12 }}
                        onClick={() => setEditingTagId(editingTagId === tag.id ? null : tag.id)}>
                        {editingTagId === tag.id ? 'Cancel' : 'Edit colour'}
                      </button>
                      <button style={{ ...common.btn, ...common.btnDanger, padding: '2px 10px', fontSize: 12 }}
                        onClick={() => handleDeleteTag(tag.id)}>Delete</button>
                    </div>
                    {editingTagId === tag.id && (
                      <div style={{ paddingLeft: 26 }}>
                        <span style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
                          Choose a new colour:
                        </span>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {TAG_COLOURS.map(c => (
                            <button key={c} type="button" style={colorSwatch(c, tag.color === c)}
                              onClick={() => handleUpdateTagColor(tag.id, c)} title={c} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ ...common.modalActions, marginTop: 24 }}>
              <button style={{ ...common.btn, ...common.btnGhost }}
                onClick={() => { setShowTagManager(false); setTagError(''); setNewTagName(''); setEditingTagId(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 },
  helpBtn: (colors) => ({
    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
    border: `1.5px solid ${colors.textMuted}`, background: 'transparent',
    color: colors.textMuted, fontSize: 12, fontWeight: 700,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s', lineHeight: 1,
  }),
};

// ── Help modal ───────────────────────────────────────────────────────────────

const HELP_CONTENT = {
  simulations: {
    title: 'Simulations: Help',
    sections: [
      {
        heading: 'What is a simulation?',
        body: 'A simulation is an interactive, step-by-step software walkthrough. Learners click through your screenshots and complete tasks, just like using the real application, without needing the software installed.',
      },
      {
        heading: 'How to build one',
        steps: [
          'Click + New Simulation and give it a title.',
          'Open the simulation and upload your screenshots, one per step in the workflow.',
          'Click Edit Hotspots on each screen to mark the clickable areas learners must interact with.',
          'Add a Tooltip (task instruction), Success message, and Fail message to each hotspot.',
          'Click ▶ Preview to experience it as a learner would.',
          'Export when ready.',
        ],
      },
      {
        heading: 'Hotspot types',
        items: [
          { label: 'Click', desc: 'Learner clicks a specific area on screen to advance.' },
          { label: 'Text input', desc: 'Learner types the correct word or phrase into a field.' },
          { label: 'Number input', desc: 'Learner types a number (e.g. a value in a settings field).' },
        ],
      },
      {
        heading: 'Exporting',
        items: [
          { label: 'Export HTML', desc: 'A single self-contained file. Open in any browser with no internet required.' },
          { label: 'Export SCORM', desc: 'A ZIP package for uploading to an LMS (Moodle, Canvas, Blackboard, etc.). Reports completion status automatically.' },
        ],
      },
      {
        heading: 'Tips',
        steps: [
          'Drag screen cards to reorder them at any time.',
          'Use Tags to organise simulations by software, team, or topic.',
          'Enable "Requires previous hotspot" to force learners to complete tasks in a set order.',
          'Screens with no hotspots advance automatically after 2 seconds.',
        ],
      },
    ],
  },
  packages: {
    title: 'Packages: Help',
    sections: [
      {
        heading: 'What is a package?',
        body: 'A package bundles multiple simulations into a single learner experience. Instead of sharing separate files, you export one file that guides learners through a full set of simulations in order, with a progress bar, transition screens, and a victory screen at the end.',
      },
      {
        heading: 'How to build one',
        steps: [
          'Click + New Package and give it a title.',
          'Select simulations from the left panel. They will appear in the right panel in the order you add them.',
          'Drag or use the ↑↓ buttons to reorder simulations.',
          'Click Save Package, then export.',
        ],
      },
      {
        heading: 'Learner experience',
        items: [
          { label: 'Progress bar', desc: 'Shows how many simulations have been completed and how many remain.' },
          { label: 'Transition screen', desc: 'Appears between simulations with the name of the next one.' },
          { label: 'Sequential order', desc: 'Each simulation must be fully completed before the next one unlocks.' },
          { label: 'Victory screen', desc: 'Shown at the end with stats including total simulations completed and time taken.' },
        ],
      },
      {
        heading: 'Exporting',
        items: [
          { label: 'Export HTML', desc: 'A self-contained file containing all simulations. Open in any browser.' },
          { label: 'Export SCORM', desc: 'Upload to your LMS. Reports as complete only when all simulations are finished.' },
        ],
      },
    ],
  },
};

function HelpModal({ topic, onClose, colors, common, mode }) {
  const content = HELP_CONTENT[topic];
  const closeBtnRef = useRef(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // indigo-300 (#a5b4fc) has ~6.6:1 contrast on dark card; indigo-700 (#4338ca) on white
  const headingColor = mode === 'dark' ? '#a5b4fc' : '#4338ca';
  const h3Style    = { color: headingColor, fontSize: 14, fontWeight: 700, margin: '20px 0 8px' };
  const pStyle     = { color: colors.textPrimary, fontSize: 14, lineHeight: 1.6, margin: 0 };
  const liStyle    = { color: colors.textPrimary, fontSize: 14, lineHeight: 1.6, marginBottom: 4 };
  const labelStyle = { color: colors.textPrimary, fontWeight: 700 };

  return (
    <div style={common.overlay} role="dialog" aria-modal="true" aria-labelledby="help-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...common.modal, maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 id="help-modal-title" style={{ ...common.modalTitle, marginBottom: 0 }}>{content.title}</h2>
          <button ref={closeBtnRef} onClick={onClose} aria-label="Close help"
            style={{ ...common.btn, ...common.btnGhost, padding: '4px 10px', fontSize: 16, flexShrink: 0 }}>
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', paddingRight: 4 }}>
          {content.sections.map((section, i) => (
            <div key={i}>
              <h3 style={h3Style}>{section.heading}</h3>
              {section.body && <p style={pStyle}>{section.body}</p>}
              {section.steps && (
                <ol style={{ paddingLeft: 20, margin: 0 }}>
                  {section.steps.map((step, j) => (
                    <li key={j} style={liStyle}>{step}</li>
                  ))}
                </ol>
              )}
              {section.items && (
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  {section.items.map((item, j) => (
                    <li key={j} style={liStyle}>
                      <span style={labelStyle}>{item.label}:</span> {item.desc}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div style={{ ...common.modalActions, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${colors.borderColor}` }}>
          <button style={{ ...common.btn, ...common.btnPrimary }} onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}
